import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import type { SelectChangeEvent } from '@mui/material/Select';

import { getProviderSettingsAction, updateProviderSettingsAction } from 'domain/paySettings/actions';

import Select from 'components/mui/Form/Select/SelectBase';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import useDispatchAction from 'hooks/useDispatchAction';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';

interface ProviderSettingOption {
  value: string;
  label: string;
}

interface ProviderSettingConfig {
  name: string;
  value: unknown;
  fieldLabel?: string;
  fieldDescription?: string;
  options?: ProviderSettingOption[];
}

interface GeneralInfoProps {
  providerId: string;
}

const GeneralInfo: React.FC<GeneralInfoProps> = ({ providerId }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const payload = useMemo(() => ({ id: providerId }), [providerId]);

  const { data: settings, isLoading, setData: updateSettings } = useDispatchAction(getProviderSettingsAction, payload);

  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [initialValues, setInitialValues] = useState<Record<string, unknown>>({});
  const [updating, setUpdating] = useState(false);

  const handleSave = useCallback(() => {
    setUpdating(true);
    new Promise((resolve, reject) => {
      dispatch(
        updateProviderSettingsAction({
          payload: { ...formValues, id: providerId },
          resolve,
          reject,
        }),
      );
    })
      .then((providerSettings: unknown) => {
        updateSettings(providerSettings);
      })
      .finally(() => setUpdating(false));
  }, [dispatch, updateSettings, providerId, setUpdating, formValues]);

  const hasChanges = useMemo(() => {
    return Object.keys(initialValues).some((key) => formValues[key] !== initialValues[key]);
  }, [formValues, initialValues]);

  const handleChange = (fieldName: string) => (event: SelectChangeEvent<unknown>) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: event.target.value,
    }));
  };

  useEffect(() => {
    if (settings) {
      const initial: Record<string, unknown> = {};
      Object.entries(settings as Record<string, ProviderSettingConfig>).forEach(([, value]) => {
        initial[value.name] = value.value;
      });
      setFormValues(initial);
      setInitialValues(initial);
    }
  }, [settings]);

  if (!settings || Object.keys(settings).length === 0 || isLoading) {
    return <LinearProgress />;
  }

  return (
    <Stack direction="column" p={3} spacing={3} justifyContent="flex-start">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1" fontWeight={500}>
          {formatMessage({
            id: 'configurations.company.paymentProviders.detailsModal.tabs.generalInfo',
            defaultMessage: 'Generail Information',
          })}
        </Typography>
        <Button variant="contained" onClick={handleSave} disabled={isLoading || updating || !hasChanges}>
          {formatMessage({ id: 'button.save', defaultMessage: 'Save' })}
        </Button>
      </Stack>
      <Stack direction="column" spacing={3} width={400}>
        {Object.entries(settings as Record<string, ProviderSettingConfig>).map(([key, config]) => (
          <FormControl key={key}>
            <Select
              id={key}
              value={formValues[config.name] || ''}
              label={config?.fieldLabel ?? ''}
              onChange={handleChange(config?.name)}
              options={config?.options ?? []}
            />
            {config?.fieldDescription && (
              <Box sx={{ fontSize: 12, pl: 1, color: 'text.secondary' }}>{config?.fieldDescription}</Box>
            )}
          </FormControl>
        ))}
      </Stack>
    </Stack>
  );
};

export default GeneralInfo;
